import { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/auth';
import {
  LayoutDashboard,
  BookTemplate,
  Brain,
  Database,
  LogOut,
  TrendingUp,
  Bot,
  Wallet,
  ChevronDown,
  Bell,
  Settings,
  HelpCircle,
  Menu,
  X,
  Zap,
  Layers,
  MessageSquare,
  Target,
  TestTube,
  FileCode,
  Coins,
  BarChart2,
  Lock,
  Activity,
  PieChart,
  BellRing,
  ArrowDownToLine,
  ArrowUpFromLine,
  History,
} from 'lucide-react';
import { LucideIcon } from 'lucide-react';
import { cn } from '../utils/cn';
import AIAssistant from './AIAssistant';

interface NavItemType {
  path: string;
  label: string;
  icon: LucideIcon;
  badge?: string;
}

const tradingItems: NavItemType[] = [
  { path: '/trade', label: 'Spot Trading', icon: TrendingUp },
  { path: '/scanner', label: 'Market Scanner', icon: Activity, badge: 'NEW' },
  { path: '/alerts', label: 'Price Alerts', icon: BellRing },
  { path: '/portfolio', label: 'Portfolio', icon: PieChart },
  { path: '/history', label: 'History', icon: History },
  { path: '/bots', label: 'Deployed Bots', icon: Bot, badge: 'LIVE' },
  { path: '/wallets', label: 'Wallets', icon: Wallet },
  { path: '/deposit', label: 'Deposit', icon: ArrowDownToLine },
  { path: '/withdraw', label: 'Withdraw', icon: ArrowUpFromLine },
  { path: '/defi', label: 'DeFi & Staking', icon: Coins },
  { path: '/token-metrics', label: 'HBX Metrics', icon: BarChart2 },
];

const automationItems: NavItemType[] = [
  { path: '/', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/strategies', label: 'Strategy Templates', icon: Target },
  { path: '/workflow/new', label: 'Visual Builder', icon: FileCode, badge: 'NEW' },
  { path: '/backtest', label: 'Backtester', icon: TestTube },
  { path: '/templates', label: 'Workflow Library', icon: BookTemplate },
];

const aiItems: NavItemType[] = [
  { path: '/models', label: 'AI Models', icon: Brain },
  { path: '/datasets', label: 'Datasets', icon: Database },
];

const communityItems: NavItemType[] = [
  { path: '/chat', label: 'Community Chat', icon: MessageSquare },
];

export default function Layout() {
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const NavSection = ({ title, items }: { title: string; items: NavItemType[] }) => (
    <div className="mb-6">
      <div className="px-3 mb-2 text-[10px] font-semibold text-[#484f58] uppercase tracking-widest">
        {title}
      </div>
      <div className="space-y-1">
        {items.map((item) => {
          const Icon = item.icon;
          const isActive = item.path === '/' 
            ? location.pathname === '/' 
            : location.pathname.startsWith(item.path);
          return (
            <Link
              key={item.path}
              to={item.path}
              className={cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 relative',
                isActive
                  ? 'bg-gradient-to-r from-[#00d4aa]/15 to-[#00d4aa]/5 text-[#00d4aa] border border-[#00d4aa]/20'
                  : 'text-[#8b949e] hover:text-[#f0f6fc] hover:bg-white/[0.04]'
              )}
            >
              {isActive && (
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-[60%] bg-[#00d4aa] rounded-r" />
              )}
              <Icon className={cn('h-[18px] w-[18px]', isActive && 'text-[#00d4aa]')} />
              {sidebarOpen && (
                <>
                  <span className="flex-1">{item.label}</span>
                  {item.badge && (
                    <span className={cn(
                      'px-2 py-0.5 text-[10px] font-bold rounded-full',
                      item.badge === 'NEW' ? 'bg-[#10b981]/20 text-[#10b981] border border-[#10b981]/30' :
                      item.badge === 'LIVE' ? 'bg-[#00d4aa]/20 text-[#00d4aa] border border-[#00d4aa]/30 animate-pulse' :
                      item.badge === 'PRO' ? 'bg-[#6366f1]/20 text-[#6366f1] border border-[#6366f1]/30' :
                      'bg-white/10 text-white'
                    )}>
                      {item.badge}
                    </span>
                  )}
                </>
              )}
            </Link>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="h-screen flex bg-[#080b0f]">
      {/* Sidebar */}
      <aside
        className={cn(
          'flex flex-col border-r border-white/[0.08] bg-[#0d1117] transition-all duration-300',
          sidebarOpen ? 'w-64' : 'w-[72px]'
        )}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-white/[0.08]">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#00d4aa] to-[#6366f1] flex items-center justify-center shadow-lg shadow-[#00d4aa]/20">
              <Zap className="h-5 w-5 text-white" />
            </div>
            {sidebarOpen && (
              <div>
                <span className="font-bold text-base text-white tracking-tight">Continental</span>
                <span className="text-[10px] text-[#00d4aa] block -mt-0.5 font-medium">DEX</span>
              </div>
            )}
          </Link>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-1.5 rounded-lg text-[#8b949e] hover:text-white hover:bg-white/[0.06] transition-colors"
          >
            {sidebarOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>

        {/* Navigation - with visible scrollbar */}
        <nav className="flex-1 overflow-y-auto custom-scrollbar py-4 px-3">
          <NavSection title="Bot Builder" items={automationItems} />
          <NavSection title="Trading" items={tradingItems} />
          <NavSection title="AI Tools" items={aiItems} />
          <NavSection title="Community" items={communityItems} />
        </nav>

        {/* Quick Actions (collapsed) */}
        {!sidebarOpen && (
          <div className="px-3 py-4 border-t border-white/[0.08] space-y-2">
            {[...automationItems, ...tradingItems].slice(0, 4).map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname.startsWith(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={cn(
                    'flex items-center justify-center p-2.5 rounded-xl transition-all',
                    isActive
                      ? 'bg-[#00d4aa]/15 text-[#00d4aa] border border-[#00d4aa]/20'
                      : 'text-[#8b949e] hover:text-white hover:bg-white/[0.06]'
                  )}
                  title={item.label}
                >
                  <Icon className="h-5 w-5" />
                </Link>
              );
            })}
          </div>
        )}

        {/* Network Status */}
        {sidebarOpen && (
          <div className="px-4 py-4 border-t border-white/[0.08]">
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-[#10b981] animate-pulse shadow-lg shadow-[#10b981]/50" />
                <span className="text-[#8b949e] font-medium">Connected</span>
              </div>
              <span className="text-[#484f58] font-mono">Multi-chain</span>
            </div>
          </div>
        )}
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="h-16 flex items-center justify-between px-6 border-b border-white/[0.08] bg-[#0d1117]/80 backdrop-blur-xl">
          <div className="flex items-center gap-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search strategies, pairs..."
                className="w-80 bg-[#161b22] border border-white/[0.08] rounded-xl px-4 py-2.5 text-sm text-white placeholder-[#484f58] focus:outline-none focus:border-[#00d4aa]/50 focus:shadow-lg focus:shadow-[#00d4aa]/10 transition-all"
              />
              <kbd className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-[#484f58] bg-[#080b0f] px-2 py-1 rounded-md font-mono">
                ⌘K
              </kbd>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Network Selector */}
            <button className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#161b22] border border-white/[0.08] text-sm hover:bg-[#21262d] hover:border-white/[0.12] transition-all">
              <div className="w-2 h-2 rounded-full bg-[#627eea] shadow-lg shadow-[#627eea]/50" />
              <span className="text-white font-medium">Ethereum</span>
              <ChevronDown className="h-4 w-4 text-[#8b949e]" />
            </button>

            {/* Notifications */}
            <button className="relative p-2.5 rounded-xl text-[#8b949e] hover:text-white hover:bg-white/[0.06] transition-colors">
              <Bell className="h-5 w-5" />
              <span className="absolute top-1.5 right-1.5 w-2.5 h-2.5 bg-[#ef4444] rounded-full border-2 border-[#0d1117] animate-pulse" />
            </button>

            {/* User menu */}
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-white/[0.06] transition-colors"
              >
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#00d4aa] to-[#6366f1] flex items-center justify-center text-white font-bold text-sm shadow-lg shadow-[#00d4aa]/20">
                  {user?.email?.[0]?.toUpperCase() || 'U'}
                </div>
                <ChevronDown className={cn(
                  'h-4 w-4 text-[#8b949e] transition-transform duration-200',
                  userMenuOpen && 'rotate-180'
                )} />
              </button>

              {userMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)} />
                  <div className="absolute right-0 mt-2 w-60 bg-[#161b22] border border-white/[0.1] rounded-xl shadow-2xl shadow-black/50 z-50 py-2 overflow-hidden animate-scale-in">
                    <div className="px-4 py-3 border-b border-white/[0.08]">
                      <div className="text-sm font-medium text-white">{user?.email}</div>
                      <div className="text-xs text-[#00d4aa] font-medium mt-0.5">Pro Account</div>
                    </div>
                    <Link to="/settings" className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#f0f6fc] hover:bg-white/[0.06] transition-colors">
                      <Settings className="h-4 w-4 text-[#8b949e]" />
                      Settings
                    </Link>
                    <a href="https://docs.hybridx.io" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#f0f6fc] hover:bg-white/[0.06] transition-colors">
                      <HelpCircle className="h-4 w-4 text-[#8b949e]" />
                      Documentation
                    </a>
                    <div className="border-t border-white/[0.08] mt-2 pt-2">
                      <button
                        onClick={logout}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#ef4444] hover:bg-[#ef4444]/10 transition-colors"
                      >
                        <LogOut className="h-4 w-4" />
                        Logout
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>

        {/* AI Assistant */}
        <AIAssistant />
      </div>
    </div>
  );
}
